import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { GoToPage } from "@similarweb/icons";
import { MenuCell } from "@similarweb/pro-logic-components";
import type { MenuItem } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { translate } from "app/filters/i18nFilter";
import { navigateToReport } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { IAiTrackerCampaign, LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { EAvailableTabs } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

const BASE_MENU_CELL_TEXT_KEY = "overview_report.topics.visibility.widget.column.topic";

const MENU_ITEM_IDS = {
    prompts: "prompts",
    sources: "sources",
} as const;

const MENU_ITEM_STATE: Record<(typeof MENU_ITEM_IDS)[keyof typeof MENU_ITEM_IDS], EAvailableTabs> = {
    [MENU_ITEM_IDS.prompts]: EAvailableTabs.PROMPTS,
    [MENU_ITEM_IDS.sources]: EAvailableTabs.SOURCES,
};

const MENU_ITEM_AVAILABILITY: Record<(typeof MENU_ITEM_IDS)[keyof typeof MENU_ITEM_IDS], boolean> = {
    [MENU_ITEM_IDS.prompts]: true,
    [MENU_ITEM_IDS.sources]: true,
};

const MENU_ITEM_ICON = (
    <GoToPage
        size="xs"
        color={colorsPalettes.carbon[200]}
    />
);

const getMenuCellItems = (): Array<MenuItem> => {
    return Object.keys(MENU_ITEM_IDS)
        .filter((key) => {
            const itemId = key as keyof typeof MENU_ITEM_IDS;
            return MENU_ITEM_AVAILABILITY[itemId];
        })
        .map((key) => ({
            id: key,
            icon: MENU_ITEM_ICON,
            text: translate(`${BASE_MENU_CELL_TEXT_KEY}.${key}`),
        }));
};

interface INavigationParams {
    campaign: IAiTrackerCampaign;
    topic: string;
}

export const TopicMenuCell = ({ campaign, topic }: INavigationParams) => {
    const menuCellItems = useMemo(() => getMenuCellItems(), []);
    const {
        params: { model },
    } = useCurrentStateAndParams();

    const handleItemClick = (item: MenuItem) => {
        if (!item?.id) {
            return;
        }

        const tab = MENU_ITEM_STATE[item.id as keyof typeof MENU_ITEM_STATE];
        navigateToReport(campaign, model as LLModel, tab, { topic });
    };

    return (
        <MenuCell
            width={"250px"}
            label={topic}
            items={menuCellItems}
            onItemClick={handleItemClick}
        />
    );
};
